<script setup lang="ts">
import { computed, ref, watch, onMounted } from 'vue';
import { useIntersectionObserver, useWindowSize } from '@vueuse/core';

import { FlightPlusHotelBanner } from '@apps/main/new-desktop/modules/flightPlusHotelBanner';
import {
  useBrowserStore,
  usePaxStore,
  useRecipeStore,
} from '@apps/main/new-desktop/core/stores';
import { useMapStore } from '@apps/main/new-desktop/core/stores/mapStore/mapStore';

import { isFeatureToggleActive } from '@libs/feature-toggle';

import {
  useTripOverviewPageStore,
  useTripOverviewPanelsStore,
  useTripSummaryStore,
} from '../../stores';
import TripOverviewMap from '../TripOverviewMap.vue';

import TripSummaryColumnMobileLoader from './TripSummaryColumnMobileLoader.vue';
import TripSummaryColumnFooter from './TripSummaryColumnFooter.vue';
import TripPriceSummary from './TripPriceSummary.vue';

const tripSummaryStore = useTripSummaryStore();
const paxStore = usePaxStore();
const tripOverviewPanelsStore = useTripOverviewPanelsStore();
const tripOverviewPageStore = useTripOverviewPageStore();
const browserStore = useBrowserStore();
const mapStore = useMapStore();
const recipeStore = useRecipeStore();

const tripSummaryRef = ref(null);
const tripSummaryColumnRef = ref<HTMLElement>();
const tripSummaryColumnFooterRef = ref<HTMLElement>();
const flightPlusHotelBannerRef = ref<HTMLElement>();
const { width: windowWidth, height: windowHeight } = useWindowSize();
const LOGO_ESTIMATED_HEIGHT = 175;
const fullBannerHeight = ref(0);
const hasOverflowWithLogo = ref(false);
const hasOverflowWithoutLogo = ref(false);

function checkOverflow(): void {
  const column = tripSummaryColumnRef.value;
  const footer = tripSummaryColumnFooterRef.value;
  const banner = flightPlusHotelBannerRef.value;

  if (!column || !footer) {
    return;
  }

  if (banner && !hasOverflowWithLogo.value) {
    fullBannerHeight.value = banner.scrollHeight;
  }

  const availableHeight = column.clientHeight;
  const footerHeight = footer.scrollHeight;

  hasOverflowWithLogo.value =
    footerHeight + fullBannerHeight.value > availableHeight;
  hasOverflowWithoutLogo.value =
    footerHeight + Math.max(0, fullBannerHeight.value - LOGO_ESTIMATED_HEIGHT) >
    availableHeight;
}

watch([windowWidth, windowHeight], () => {
  checkOverflow();
});

onMounted(() => {
  checkOverflow();
});

const shouldShowBanner = computed(
  () =>
    recipeStore.isSingleHotelRecipe ||
    (recipeStore.flightOnlyRecipe.length > 0 &&
      recipeStore.hotelOnlyRecipe.length === 0)
);

useIntersectionObserver(
  tripSummaryRef,
  (entries: IntersectionObserverEntry[]) => {
    if (entries[0]) {
      tripSummaryStore.isTripSummaryVisible = entries[0].isIntersecting;
    }
  }
);
</script>

<template>
  <div ref="tripSummaryColumnRef" class="trip-summary-column">
    <template v-if="browserStore.isLargerThan1199px">
      <TripOverviewMap
        v-if="!shouldShowBanner"
        class="map"
        :should-have-border-radius="false"
        @zoom="mapStore.openFullScreenDynamicTripMap()"
      />
      <div
        v-else-if="!hasOverflowWithoutLogo"
        ref="flightPlusHotelBannerRef"
        class="flight-plus-hotel-banner"
      >
        <FlightPlusHotelBanner :hide-banner="hasOverflowWithLogo" />
      </div>
      <div ref="tripSummaryColumnFooterRef">
        <TripSummaryColumnFooter
          :paxes="paxStore.getPaxesButtonSimpleText"
          :travel-dates="tripSummaryStore.travelDateRange"
          :total-price="tripSummaryStore.tripTotalPrice"
          :gross-price="tripSummaryStore.tripGrossPrice"
          :total-addons-price="tripSummaryStore.tripTotalAddonsPrice"
          :currency="tripSummaryStore.tripSummaryCurrency"
          :should-show-share-button="true"
          class="footer"
          @total-price-clicked="
            tripOverviewPanelsStore.openTripPriceSummaryPanel
          "
        />
      </div>
    </template>

    <template v-else>
      <template
        v-if="
          tripOverviewPageStore.anyItineraryLoaded &&
          !isFeatureToggleActive('loader')
        "
      >
        <div ref="tripSummaryRef">
          <TripPriceSummary
            class="trip-summary-price-summary"
            :price-segments="tripSummaryStore.tripPriceSegments"
          />

          <TripSummaryColumnFooter
            :paxes="paxStore.getPaxesButtonSimpleText"
            :travel-dates="tripSummaryStore.travelDateRange"
            :currency="tripSummaryStore.tripSummaryCurrency"
            :total-price="tripSummaryStore.tripTotalPrice"
            :gross-price="tripSummaryStore.tripGrossPrice"
            :total-addons-price="tripSummaryStore.tripTotalAddonsPrice"
            :should-show-share-button="true"
          />
        </div>
      </template>
      <template v-else>
        <TripSummaryColumnMobileLoader />
      </template>
    </template>
  </div>
</template>

<style scoped lang="scss">
.trip-summary-column {
  background-color: var(--color-white);
  height: 100vh;

  display: flex;
  flex-direction: column;
  justify-content: space-between;
  .flight-plus-hotel-banner {
    display: flex;
    justify-content: center;
    align-items: center;
    margin: auto var(--spacing-12);
  }
  .footer {
    padding: var(--spacing-12) var(--spacing-24);
    padding-bottom: var(--spacing-24);
  }

  @include for-less-or-equal-than-1199-px {
    height: auto;
    box-shadow: var(--shadow-elevation-200);
  }

  @include for-less-or-equal-than-1199-px {
    border: 1px solid var(--color-neutral-30);
  }
}
</style>
